<script setup>
import { ref, computed, onMounted, onServerPrefetch } from 'vue'
import { getArticleList } from '@/api/article.js'
import { getVideoList } from '@/api/video.js'
import { checkVideo } from '@/utils'

const props = defineProps({
  empty: { type: Boolean, default: false },
  size: { type: String, default: null },
  categoryId: { type: Number, default: null },
  categoryIsVideo: { type: Boolean, default: false },
  categoryTitle: { type: String, default: null },
  categoryPath: { type: String, default: null },
  mainCategoryPath: { type: String, default: null },
  title: { type: String, default: null },
  link: {
    type: Object,
    default() {
      return { url: '', rel: '', target: '' }
    }
  }
})

const SSR = import.meta.env.SSR ? true : false
const errorSum = ref(SSR ? 1 : 0)
const isMobile = computed(() => ['s', 'xs'].includes(props.size))
const isEmpty = computed(() => list.value.length === 0)
const list = ref([])
const _list = computed(() =>
  isMobile.value ? list.value.slice(0, 5 + errorSum.value) : list.value.slice(0, 8 + errorSum.value)
)
const showMore = computed(() =>
  isMobile.value ? list.value.length > 5 + errorSum.value : list.value.length >= 8 + errorSum.value
)
const loading = ref(true)
const handle = async () => {
  try {
    var listFunc = getArticleList
    if (props.categoryIsVideo) {
      listFunc = getVideoList
    }
    const res = await listFunc({ category_id: props.categoryId, page: 1, limit: 9 })
    if (res.code !== 0) throw new Error(res.message)
    list.value = res.data?.list || []
    loading.value = false
  } catch (error) {
    loading.value = false
  }
}
onMounted(handle)
onServerPrefetch(handle)

const adsError = () => {
  errorSum.value++
}
</script>

<template>
  <section
    v-if="loading ? true : !isEmpty"
    class="tvb-category-model"
    :class="size ? 'model-' + size : ''"
    :id="'tvb-category-model-' + categoryId"
  >
    <div class="model-h">
      <template v-if="loading">
        <div class="tvb-skeleton title" style="width: 20%"></div>
        <div class="tvb-skeleton more"></div>
      </template>
      <template v-else>
        <h2 class="clear-h title" :title="title">
          <tvb-router-link :to="link.url" :linkRel="link.rel" :linkTarget="link.target">
            {{ title }}
          </tvb-router-link>
        </h2>
        <tvb-button
          v-if="showMore"
          class="more"
          :type="!isMobile ? 'more' : 'more-text'"
          :animation="!isMobile ? 'more' : ''"
          icon="icon_right_more"
          :path="link.url"
          >查看更多</tvb-button
        >
      </template>
    </div>
    <div class="model-content">
      <template v-if="loading">
        <tvb-article-card
          v-for="i in 6"
          :key="i"
          :size="size"
          :autoheight="isMobile"
          :loading="loading"
        ></tvb-article-card>
      </template>
      <template v-else>
        <template v-for="(item, index) in _list" :key="item.id">
          <tvb-ads
            v-if="
              index + 1 === 3 &&
              mainCategoryPath &&
              _list.length > 2 &&
              ['xl', 'l'].includes(size) &&
              !SSR
            "
            ad-type="lrec"
            :position="categoryIsVideo ? 12 : 7"
            class="tvb-article-card card-xl tvb-hairline--surround"
            :unit="`/${mainCategoryPath}`"
            @error="adsError"
            style="
              background-color: #f8f9fa;
              border-radius: 8px;
              --tvb-hairline-border-radius: calc(2 * 8px);
            "
            :params="{
              content_type: 'content' || 'x',
              main_category_path: mainCategoryPath
            }"
          />
          <tvb-article-card
            :size="size"
            :autoheight="isMobile"
            :img="item.image_info"
            :isVideo="categoryIsVideo || checkVideo(item)"
            :videoType="categoryIsVideo"
            :playIcon="{ size: isMobile ? 40 : 48 }"
            :title="item.title"
            :subtitle="item.category_name"
            :description="categoryIsVideo ? item.description : ''"
            :date="item.publish_at"
            :link="{ url: item.link, rel: item.link_rel, target: item.link_target }"
          ></tvb-article-card>
        </template>
      </template>
    </div>
    <tvb-button
      v-if="!loading && isMobile && showMore"
      type="big-more"
      animation=""
      icon="icon_right_more"
      :path="link.url"
    >
      查看更多
    </tvb-button>
  </section>
</template>

<style lang="scss" scoped>
.tvb-category-model {
  margin-top: 40px;
  .model-h {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    .title {
      height: 32px;
      line-height: 32px;
      color: #231f20;
      font-size: 22px;
      font-weight: 500;
      letter-spacing: 0.5px;
      @include text-ellipsis();

      a {
        text-decoration: none;
        background-color: transparent;
        color: inherit;
        transition: color 0.15s linear;
        cursor: pointer;
        &:visited {
          color: inherit;
        }
      }
    }
    .more {
      --tvb-button-height: 36px;
      width: 97px;
      margin-left: 12px;
      flex: none;
      &.tvb-skeleton {
        height: var(--tvb-button-height);
      }
    }
  }

  .model-content {
    --gap: 16px;
    --columns: 3;
    gap: var(--gap);
    display: grid;
    grid-template-columns: repeat(var(--columns), 1fr);
    margin-bottom: var(--gap);
    :deep(.tvb-ads) {
      height: 100% !important;

      .tvb-ads_container {
        align-items: center;
      }
    }
  }

  &.model-s,
  &.model-xs {
    margin-top: 32px;
    .model-h {
      margin-bottom: 10px;
      .title {
        height: 24px;
        line-height: 24px;
        font-size: 17px;
      }
      .more {
        --tvb-button-height: 20px;
        width: auto;
        > :deep(.span) {
          --tvb-button-padding: 0px;
        }
        &.tvb-skeleton {
          height: 20px;
          width: 58px + 12px;
        }
      }
    }

    .model-content {
      --gap: 10px;
      --columns: 1;
    }
  }
}
</style>
